// ============================================================================
// RequireStaff — route guard for the Ops Console (US-004)
//
// Permits only authenticated users whose role is 'admin' or 'super_admin'.
// - Waits for token rehydration before deciding, so refreshing on a deep /ops
//   route does not bounce a valid staff user to /login.
// - Unauthenticated  → /login
// - Authenticated non-staff → /sites, WITHOUT confirming the console exists.
//
// This is client-side convenience only. Every /api/v1/admin/* endpoint must
// independently enforce the role server-side (see the PRD's server notes).
// ============================================================================

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Outlet } from 'react-router-dom';
import { rehydrate } from '../../reducers/authSlice';

export const STAFF_ROLES = ['admin', 'super_admin'];
export const isStaff = (user) => Boolean(user) && STAFF_ROLES.includes(user.role);
export const isSuperAdmin = (user) => Boolean(user) && user.role === 'super_admin';

// NOTE: the REACT_APP_OPS_PREVIEW bypass was removed at the live cutover. It
// existed to preview the console with no backend and no login, which the
// fixture layer made possible. With every resource live, the synthetic staff
// user it seeded carries no token, so it would pass this guard and then 401 on
// every request — a guard bypass that no longer previews anything.

export default function RequireStaff() {
  const dispatch = useDispatch();
  const auth = useSelector((s) => s.auth);
  const { user, isLoggedIn, isLoading, hasCheckedRehydrate } = auth;

  useEffect(() => {
    if (!isLoggedIn && !hasCheckedRehydrate) dispatch(rehydrate());
  }, [dispatch, isLoggedIn, hasCheckedRehydrate]);

  // Hold rendering until we know who the user is.
  if (!hasCheckedRehydrate || isLoading) {
    return (
      <div className="sa" style={{ display: 'grid', placeItems: 'center' }}>
        <div className="sa-cell-mute">Loading…</div>
      </div>
    );
  }

  if (!isLoggedIn) return <Navigate to="/login" replace />;
  // Non-staff: send to the customer app; do not reveal that /ops exists.
  if (!isStaff(user)) return <Navigate to="/sites" replace />;

  return <Outlet />;
}
